import React from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FiPlus } from 'react-icons/fi';
import InfoTooltip from '@core/components/InfoTooltip';
import { FormFieldErrors } from '@core/hooks/useFormModal';

export interface FormModalFieldOption {
  label: string;
  value: string | number;
}

export interface FormModalField<T> {
  name: keyof T;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'datetime-local' | 'number' | 'checkbox' | 'switch';
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  rows?: number;
  options?: FormModalFieldOption[];
  /** Escape hatch for complex fields (e.g. dependent selects) — renders instead of the built-ins. */
  render?: (ctx: {
    value: any;
    setValue: (v: unknown) => void;
    error?: string[];
    busy: boolean;
  }) => React.ReactNode;
}

export interface FormModalProps<T extends Record<string, any>> {
  show: boolean;
  onHide: () => void;
  title: React.ReactNode;
  icon?: React.ReactNode;
  fields: FormModalField<T>[];
  form: T;
  formErrors: FormFieldErrors<T>;
  busy: boolean;
  onChange: (
    field: keyof T
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  setFieldValue: (field: keyof T, value: unknown) => void;
  onSubmit: () => void;
  submitLabel?: string;
  busyLabel?: string;
  submitIcon?: React.ReactNode;
  size?: 'sm' | 'lg' | 'xl';
  testId?: string;
}

/**
 * Config-driven create/edit modal. Renders a react-bootstrap Modal + Form from
 * a list of field descriptors, removing the per-modal boilerplate. Drive its
 * state with {@link useFormModal}. Complex fields can supply a `render` escape
 * hatch instead of the built-in input types.
 */
export function FormModal<T extends Record<string, any>>({
  show,
  onHide,
  title,
  icon,
  fields,
  form,
  formErrors,
  busy,
  onChange,
  setFieldValue,
  onSubmit,
  submitLabel = 'Save',
  busyLabel = 'Saving…',
  submitIcon = <FiPlus className="me-2" />,
  size = 'lg',
  testId,
}: FormModalProps<T>) {
  const renderField = (field: FormModalField<T>) => {
    const error = formErrors[field.name];
    const isInvalid = !!error;
    const value = (form[field.name] ?? '') as any;

    const label = (
      <Form.Label>
        {field.label}
        {field.required ? <span className="text-danger">*</span> : null}
        {field.helpText ? <InfoTooltip text={field.helpText} /> : null}
      </Form.Label>
    );

    if (field.render) {
      return (
        <Form.Group className="mb-3" controlId={String(field.name)} key={String(field.name)}>
          {label}
          {field.render({
            value: form[field.name],
            setValue: (v) => setFieldValue(field.name, v),
            error,
            busy,
          })}
        </Form.Group>
      );
    }

    if (field.type === 'checkbox' || field.type === 'switch') {
      return (
        <Form.Group className="mb-3" controlId={String(field.name)} key={String(field.name)}>
          <Form.Check
            type={field.type}
            label={field.label}
            checked={!!form[field.name]}
            disabled={busy}
            onChange={(e) => setFieldValue(field.name, e.target.checked)}
          />
        </Form.Group>
      );
    }

    return (
      <Form.Group className="mb-3" controlId={String(field.name)} key={String(field.name)}>
        {label}
        {field.type === 'textarea' ? (
          <Form.Control
            as="textarea"
            rows={field.rows ?? 3}
            placeholder={field.placeholder}
            value={value}
            onChange={onChange(field.name)}
            isInvalid={isInvalid}
          />
        ) : field.type === 'select' ? (
          <Form.Select
            value={value}
            onChange={onChange(field.name)}
            isInvalid={isInvalid}
          >
            {field.placeholder ? <option value="">{field.placeholder}</option> : null}
            {(field.options ?? []).map((opt) => (
              <option key={String(opt.value)} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={field.type}
            placeholder={field.placeholder}
            value={value}
            onChange={onChange(field.name)}
            isInvalid={isInvalid}
          />
        )}
        <Form.Control.Feedback type="invalid" role="alert">
          {error?.[0]}
        </Form.Control.Feedback>
      </Form.Group>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      centered
      size={size}
      data-testid={testId}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-semibold">
          {icon ? <span className="me-2">{icon}</span> : null}
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <Form>
          {fields.map(renderField)}
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              size="lg"
              className="btn-action"
              onClick={onSubmit}
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <>
                  <Spinner animation="border" className="me-2" size="sm" />
                  {busyLabel}
                </>
              ) : (
                <>
                  {submitIcon}
                  {submitLabel}
                </>
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default FormModal;
